import { Container } from "@/types/v2";
import {
  getBidirectionalTechnicianIds,
  normalizeLabels,
  normalizeTechs,
} from "@/lib/checkContainerConfiguration";

export interface LinkAdditions {
  technicianIds: string[];
  labels: string[];
  /**
   * Links to take OFF (WEBV3-346). Optional, so add-only callers are
   * unchanged. The name `LinkAdditions` is historical: this is the whole
   * "link changes" payload shared by the planner, the merge and the
   * repository. A key that is both added and removed is removed (removal
   * wins), and removing a key a container does not hold is a no-op for it.
   */
  removeTechnicianIds?: string[];
  removeLabels?: string[];
}

export const hasLinkRemovals = (changes: LinkAdditions): boolean =>
  (changes.removeTechnicianIds?.length ?? 0) > 0 ||
  (changes.removeLabels?.length ?? 0) > 0;

/**
 * Absolute two-way-sync choices from a bulk edit (WEBV3-341), keyed by
 * normalized technician id / label name. `true` = sync on, `false` = off. A
 * choice applies to that link on every target container that holds it OR
 * gains it in the same merge; keys a container neither holds nor gains are
 * ignored. Absolute (not "toggle") so a retried transaction or a stale Review
 * cannot double-apply.
 */
export interface SyncOverrides {
  technicians: Record<string, boolean>;
  labels: Record<string, boolean>;
}

export const NO_SYNC_OVERRIDES: SyncOverrides = { technicians: {}, labels: {} };

export const hasSyncOverrides = (sync?: SyncOverrides): boolean =>
  !!sync &&
  (Object.keys(sync.technicians).length > 0 ||
    Object.keys(sync.labels).length > 0);

/**
 * Whether `key`'s two-way sync is on for this container right now. Technician
 * sync is opt-out (see getBidirectionalTechnicianIds), label sync opt-in.
 */
export const isLinkSyncOn = (
  kind: "technician" | "label",
  container: Container,
  key: string
): boolean =>
  kind === "technician"
    ? getBidirectionalTechnicianIds(container).includes(key)
    : normalizeLabels(container.bidirectionalLabel).includes(key);

const unique = <T>(values: T[]): T[] => Array.from(new Set(values));

const sameMembers = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

/**
 * Same narrowest-representation rule as the repository's
 * collapseBidirectionalTechnicianId: a bare string for 0-1 technicians ("" for
 * none), an array once there are 2+.
 */
const collapseTechs = (ids: string[]): string | string[] =>
  ids.length <= 1 ? (ids[0] ?? "") : ids;

/** Same rule for labels (the repository's collapseBidirectionalLabel). */
const collapseLabels = (labels: string[]): string | string[] =>
  labels.length <= 1 ? (labels[0] ?? "") : labels;

/** Applies absolute on/off choices to `start`, for links present in `linked`. */
const applyChoices = (
  start: string[],
  linked: string[],
  choices: Record<string, boolean>
): string[] => {
  let next = start;
  for (const [key, on] of Object.entries(choices)) {
    if (!linked.includes(key)) continue;
    next = on ? unique([...next, key]) : next.filter((k) => k !== key);
  }
  return next;
};

/**
 * Applies link additions and removals plus any two-way-sync choices to ONE
 * container, without mutating the input. Additions keep existing links and are
 * deduped. Returns the same container object with changed=false when there is
 * nothing to add or remove and no choice changes a value.
 *
 * Removals (WEBV3-346) drop the key from the linked list and from the
 * matching sync set. A removal alone never materializes
 * `bidirectionalTechnicianId`: `undefined` means "every linked technician
 * syncs", and dropping one leaves the rest eligible, so it stays `undefined`.
 * An already-explicit set is filtered so a removed technician cannot linger in
 * it (the subset invariant). A removal is not a sync *choice*: only additions
 * and explicit choices count as a technician sync change.
 *
 * New technician links must start with two-way sync OFF. Technician sync is
 * opt-out: `bidirectionalTechnicianId === undefined` means every linked
 * technician writes back, so simply appending would switch the new technician's
 * sync ON. So, whenever a technician is added or a technician choice changes a
 * value, the currently-effective sync set is materialized into
 * `bidirectionalTechnicianId` first; a new technician lands outside it unless
 * its own choice is on. Labels are opt-in (`bidirectionalLabel` unset = no
 * write-back), so a new label needs no such step. A choice equal to the current
 * value is not a change, so a legacy container is never rewritten for nothing.
 * This is the single merge used by both the repository write and the Review
 * conflict preview.
 */
export function mergeLinksIntoContainer(
  container: Container,
  additions: LinkAdditions,
  sync: SyncOverrides = NO_SYNC_OVERRIDES
): { container: Container; changed: boolean } {
  const currentTechs = normalizeTechs(container.linkedTechnicianId);
  const currentLabels = normalizeLabels(container.linkedSourceLabel);

  // Removal wins over an add for the same key, held or not.
  const removeTechSet = new Set(
    (additions.removeTechnicianIds ?? []).map(String)
  );
  const removeLabelSet = new Set(additions.removeLabels ?? []);
  const removedTechs = currentTechs.filter((id) => removeTechSet.has(id));
  const removedLabels = currentLabels.filter((label) =>
    removeLabelSet.has(label)
  );
  const droppedTechs = new Set(removedTechs);
  const droppedLabels = new Set(removedLabels);

  const newTechs = unique(additions.technicianIds.map(String)).filter(
    (id) => !currentTechs.includes(id) && !removeTechSet.has(id)
  );
  const newLabels = unique(additions.labels).filter(
    (label) => !currentLabels.includes(label) && !removeLabelSet.has(label)
  );
  const nextTechs = [
    ...currentTechs.filter((id) => !droppedTechs.has(id)),
    ...newTechs,
  ];
  const nextLabels = [
    ...currentLabels.filter((label) => !droppedLabels.has(label)),
    ...newLabels,
  ];

  // Technician sync starts from what is effective now MINUS the removed
  // links, and a change is measured against that start, so a removal alone is
  // not a sync change (see the doc comment).
  const currentTechSync = getBidirectionalTechnicianIds(container);
  const startTechSync = currentTechSync.filter((id) => !droppedTechs.has(id));
  const nextTechSync = applyChoices(startTechSync, nextTechs, sync.technicians);
  const techSyncChanged = !sameMembers(nextTechSync, startTechSync);

  // Label sync is opt-in (unset = none), so dropping a synced label IS a real
  // change to the stored set and is measured against the current one.
  const currentLabelSync = normalizeLabels(container.bidirectionalLabel);
  const startLabelSync = currentLabelSync.filter(
    (label) => !droppedLabels.has(label)
  );
  const nextLabelSync = applyChoices(startLabelSync, nextLabels, sync.labels);
  const labelSyncChanged = !sameMembers(nextLabelSync, currentLabelSync);

  if (
    newTechs.length === 0 &&
    newLabels.length === 0 &&
    removedTechs.length === 0 &&
    removedLabels.length === 0 &&
    !techSyncChanged &&
    !labelSyncChanged
  ) {
    return { container, changed: false };
  }

  const next: Container = { ...container };
  if (newTechs.length > 0 || techSyncChanged) {
    next.bidirectionalTechnicianId = collapseTechs(nextTechSync);
  } else if (
    removedTechs.length > 0 &&
    container.bidirectionalTechnicianId !== undefined
  ) {
    // Already explicit: keep the subset invariant. (Undefined stays undefined.)
    next.bidirectionalTechnicianId = collapseTechs(nextTechSync);
  }
  if (newTechs.length > 0 || removedTechs.length > 0) {
    next.linkedTechnicianId = nextTechs;
  }
  if (newLabels.length > 0 || removedLabels.length > 0) {
    next.linkedSourceLabel = nextLabels;
  }
  if (labelSyncChanged) {
    next.bidirectionalLabel = collapseLabels(nextLabelSync);
  }
  return { container: next, changed: true };
}
